import React from 'react';
import { useNavigate } from 'react-router-dom';

const Home = () => {
  const navigate = useNavigate();
  const appVersion = process.env.REACT_APP_VERSION;

  return (
    <div className="min-h-screen h-auto flex flex-col relative bg-slate-50 overflow-hidden">
      {/* Background Image */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat opacity-60 z-0"
        style={{ backgroundImage: 'url("/background.jpg")' }}
      />
      
      {/* Background Gradient */}
      <div className="fixed inset-0 bg-gradient-to-b from-white/70 to-white/15 z-0" />

      {/* Header */}
      <div className="py-2 md:py-4 px-3 md:px-8 bg-white border-b border-gray-300 text-black rounded-none z-[6] sticky top-0 backdrop-blur-md">
        <div className="flex items-center max-w-7xl mx-auto gap-2 md:gap-4 relative w-full">
          <img 
            src="/deped-logo.png" 
            alt="DepEd Logo" 
            className="h-8 sm:h-9 md:h-14"
          />
          <h1 className="font-extrabold text-slate-800 text-sm sm:text-lg md:text-xl font-poppins tracking-tight">
            Ticketing System
          </h1>

          <div className="ml-auto flex gap-2">
            <button 
              aria-label="Admin Login"
              className="text-slate-900 bg-slate-100/80 hover:bg-slate-200 p-2 rounded-lg transition-colors"
              onClick={() => navigate('/login')}
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative z-10 py-8 sm:py-16 md:py-20 lg:py-24 px-3 md:px-8 text-center bg-transparent flex flex-col items-center justify-center flex-1">
        <div className="max-w-4xl w-full">
          <h2 className="font-extrabold text-lg sm:text-2xl md:text-3xl lg:text-4xl leading-tight mb-3 sm:mb-4 text-slate-900 font-poppins tracking-tight">
            Welcome to <span className="text-blue-600">Dep</span><span className="text-red-500">ED</span><br /> Imus City{' '}
            <span className="text-blue-600 relative ml-1 after:content-[''] after:absolute after:-bottom-1 after:left-0 after:w-full after:h-2 after:bg-blue-600/10 after:-z-10 after:rounded">
              Ticketing
            </span>
          </h2>

          <div className="flex flex-col sm:flex-row gap-4 sm:gap-4 justify-center mb-8 sm:mb-12 mt-8 sm:mt-12 w-full">
            <button
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 sm:py-4 px-4 sm:px-6 rounded-lg text-sm sm:text-base font-poppins hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-600/30 transition-all duration-200 w-full sm:w-auto flex items-center justify-center gap-2"
              onClick={() => navigate('/tickets')}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              Submit a New Ticket
            </button>
            
            <button
              className="text-white bg-black border border-black font-semibold py-3 sm:py-4 px-4 sm:px-6 rounded-lg text-sm sm:text-base font-poppins hover:border-gray-600 hover:bg-gray-600 transition-colors w-full sm:w-auto flex items-center justify-center gap-2"
              onClick={() => navigate('/track-ticket')}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
              Track Your Ticket
            </button>
          </div>
          
          <div className="max-w-xs mx-auto border-t border-slate-200 mb-8" />
        </div>
      </div>

      {/* Footer */}
      <footer className="py-4 sm:py-6 md:py-8 px-3 md:px-6 text-center bg-slate-50/80 backdrop-blur-md border-t border-slate-200 text-slate-600 z-10 mt-auto">
        <div className="max-w-4xl mx-auto">
          <div className="mb-3 flex justify-center items-center gap-2">
            <h3 className="font-bold text-slate-700 text-sm sm:text-base font-poppins">
              DepEd Imus City
            </h3>
          </div>
          
          <p className="text-xs sm:text-sm font-poppins mb-1">
            For support inquiries:<br className="sm:hidden" /> icts.imus@deped.gov.ph
          </p>
          
          <p className="text-xs sm:text-sm font-poppins text-slate-400">
            © {new Date().getFullYear()} Department of Education - Imus City Division
          </p>

          <p className="text-xs sm:text-sm font-poppins text-slate-400 mt-1 italic">
            version {appVersion}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home; 